import React, { useState } from 'react';
import { Quote, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import getFullUrl from '../../utils/formatUrl';

const PREVIEW_PARAGRAPH_COUNT = 2;

const FounderTalk = ({ data = {} }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const imageUrl = getFullUrl(data.image);

    const renderRole = (roleText) => {
        if (!roleText) return null;
        const lines = roleText.split('\n');
        return lines.map((line, lineIdx) => {
            const cleanedLine = line.replace(/\s+/g, ' ').trim();
            const regex = /(Arya\s+Sabhyata)/i;
            const match = cleanedLine.match(regex);
            if (match) {
                const parts = cleanedLine.split(regex);
                return (
                    <div key={lineIdx} className={lineIdx > 0 ? "mt-1" : ""}>
                        {parts.map((part, partIdx) => {
                            if (part.toLowerCase() === 'arya sabhyata') {
                                return (
                                    <span 
                                        key={partIdx} 
                                        className="text-[#cc0000] inline-block uppercase font-black"
                                    >
                                        Arya Sabhyata
                                    </span>
                                );
                            }
                            return (
                                <span key={partIdx} className="uppercase">
                                    {part}
                                </span>
                            );
                        })}
                    </div>
                );
            }
            return (
                <div key={lineIdx} className={lineIdx > 0 ? "mt-1 uppercase" : "uppercase"}>
                    {cleanedLine}
                </div>
            );
        });
    };

    const renderMessage = () => {
        const defaultMessage = `"Education is the tool with which we can change the course of destiny. At Arya Sabhyata, our journey is not just about academic results, but about creating holistic leaders who carry the torch of innovation and ethics into the world."`;

        if (!data.message) {
            return <p>{defaultMessage}</p>;
        }

        const paragraphs = data.message.split('\n').filter(p => p.trim() !== '');
        const hasMore = paragraphs.length > PREVIEW_PARAGRAPH_COUNT;
        const visibleParagraphs = isExpanded ? paragraphs : paragraphs.slice(0, PREVIEW_PARAGRAPH_COUNT);

        return (
            <>
                {visibleParagraphs.map((para, i) => (
                    <p key={i} className={i > 0 ? "mt-4" : ""}>{para}</p>
                ))}

                {/* Fade overlay when collapsed */}
                {hasMore && !isExpanded && (
                    <div className="relative mt-2">
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-50/90 to-transparent pointer-events-none rounded-r-3xl" />
                    </div>
                )}

                {/* Read More / Read Less Button */}
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(prev => !prev)}
                        className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 bg-[#cc0000] text-white text-[11px] font-black uppercase tracking-[2px] rounded-full shadow-lg hover:bg-[#003366] transition-all duration-300 group not-italic"
                    >
                        {isExpanded ? (
                            <>
                                Read Less
                                <ChevronUp size={14} className="group-hover:-translate-y-0.5 transition-transform" />
                            </>
                        ) : (
                            <>
                                Read More
                                <ChevronDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
                            </>
                        )}
                    </button>
                )}
            </>
        );
    };

    return (
        <section className="pb-12 pt-16 bg-white relative overflow-hidden">
            {/* Soft decorative background blobs */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-slate-50 rounded-full blur-[100px] -z-10 translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-[100px] -z-10 -translate-x-1/2 translate-y-1/2"></div>

            <div className="max-w-7xl mx-auto px-4">
                <div className="flex flex-col lg:flex-row gap-24 items-center">
                    
                    {/* Left Side: Founder Image with rich accents */}
                    <div className="lg:w-1/2 relative group lg:pr-10">
                        {/* Decorative Frames */}
                        <div className="absolute -top-6 -left-6 w-32 h-32 bg-slate-100 rounded-2xl -z-10 rotate-12 transition-transform group-hover:rotate-0 duration-700"></div>
                        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-red-50 rounded-2xl -z-10 -rotate-12 transition-transform group-hover:rotate-0 duration-700"></div>
                        
                        <div className="relative z-10 w-full">
                            <div className="rounded-[30px] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.2)] border-8 border-white w-full relative">
                                {imageUrl && (
                                    <img 
                                        src={imageUrl} 
                                        className="w-full h-full object-cover transition-all duration-1000 transform group-hover:scale-105" 
                                        alt={data.name || "Founder"} 
                                    />
                                )}
                                
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                            </div>

                            {/* Floating Professional Badge */}
                            <div className="absolute -bottom-8 left-6 right-auto md:left-auto md:-bottom-12 md:-right-12 bg-white p-6 md:p-8 rounded-3xl shadow-[0_20px_50px_-10px_rgba(204,0,0,0.2)] border-t-8 border-[#cc0000] md:min-w-[320px] animate-in slide-in-from-right duration-1000">
                                <h4 className="text-2xl md:text-3xl font-black text-slate-900 leading-none uppercase tracking-tighter">{data.name || 'Mr. Prem Agarwal'}</h4>
                                <div className="flex items-start gap-3 md:gap-4 mt-3 md:mt-4">
                                    <div className="h-[2px] w-6 md:w-8 bg-[#cc0000] mt-1.5 shrink-0"></div>
                                    <span className="text-[9px] md:text-[10px] font-black text-[#cc0000] tracking-[2px] md:tracking-[3px] leading-tight uppercase">
                                        {renderRole(data.role || 'Founder\nChairman\nArya Sabhyata Group')}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Stats Bubble */}
                            <div className="absolute top-12 -left-12 bg-slate-900 text-white p-6 rounded-2xl shadow-2xl hidden lg:block transform hover:scale-110 transition-transform">
                                <div className="text-2xl font-black">{data.years || '25'}+</div>
                                <div className="text-[8px] uppercase font-bold tracking-widest text-slate-400">Years of Legacy</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Message Content */}
                    <div className="lg:w-1/2 space-y-10">
                        <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                                <span className="px-4 py-1.5 bg-[#cc0000] text-white text-[10px] font-black uppercase tracking-[3px] rounded-sm">
                                    {data.badge || 'FOUNDER\'S MESSAGE'}
                                </span>
                            </div>
                            <h2 className="text-lg sm:text-xl md:text-[24px] lg:text-[28px] xl:text-[32px] font-black leading-tight text-slate-900 uppercase tracking-tighter sm:whitespace-nowrap font-sans">
                                Inspiring <span className="text-[#003366]">Excellence</span>, Shaping the <span className="relative">
                                    Future
                                    <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 100 8" preserveAspectRatio="none">
                                        <path d="M0 5C30 5 70 5 100 2" stroke="#cc0000" strokeWidth="4" fill="none" strokeLinecap="round" />
                                    </svg>
                                </span>.
                            </h2>
                        </div>

                        <div className="relative">
                            <Quote size={80} className="absolute -top-12 -left-12 text-slate-100 -z-10" />
                            <div className="text-slate-600 text-xl font-medium leading-relaxed italic border-l-[6px] border-[#cc0000] pl-10 py-4 bg-slate-50/50 rounded-r-3xl">
                                {renderMessage()}
                            </div>
                        </div>

                        <div className="pt-6 flex flex-col sm:flex-row gap-6 items-center">
                            <Link to="/about" className="flex items-center gap-4 group cursor-pointer">
                                <div className="w-14 h-14 rounded-full border-2 border-slate-200 flex items-center justify-center group-hover:border-[#cc0000] transition-colors">
                                    <div className="w-10 h-10 bg-[#cc0000] rounded-full flex items-center justify-center text-white">
                                        <Quote size={18} fill="currentColor" />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-black text-slate-900 uppercase tracking-tighter">Read Full Story</div>
                                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">25 Years of Impact</div>
                                </div>
                            </Link>
                            <Link to="/about" className="w-full sm:w-auto">
                                <button className="w-full sm:w-auto px-12 py-5 bg-slate-900 text-white font-black uppercase text-[11px] tracking-[5px] hover:bg-[#cc0000] transition-all rounded-sm shadow-xl flex items-center justify-center group">
                                    Explore Our Vision
                                    <Send size={14} className="ml-3 group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform" />
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FounderTalk;
